import apiClient from './client';

/* ------------------------------------------------------------------ */
/* Types                                                              */
/* ------------------------------------------------------------------ */

export type FlowbackStatus = 'all' | 'pending' | 'approved' | 'rejected';

export interface FlowbackRecord {
  id: number;
  project_id: number;
  project_name: string;
  requirement_id: string;
  subcategory: string;
  status: 'pending' | 'approved' | 'rejected';
  initiator: string;
  reviewer: string;
  mainline_req_id: string;
  merge_order_seq: number | null;
  created_at: string;
  reviewed_at: string | null;
}

export interface FlowbackDetail {
  record: FlowbackRecord;
  source: { space: string; subcategory: string; body: Record<string, unknown> | null };
  target: { space: string; subcategory: string; body: Record<string, unknown> | null };
  direction: string;
}

export interface FlowbackStatsItem {
  key: string;
  count: number;
  breakdown: { pending: number; approved: number; rejected: number };
}

export interface FlowbackStatsResponse {
  dimension: string;
  items: FlowbackStatsItem[];
}

export interface LaggingRequirement {
  requirement_id: string;
  subcategory: string;
  mainline_req_id: string;
  last_mainline_version: string | null;
  last_action: 'synced' | 'ignored' | null;
}

export interface NewRequirementItem {
  requirement_id: string;
  subcategory: string;
}

export interface FlowbackEligibilityItem {
  requirement_id: string;
  subcategory: string;
}

export interface RequirementSyncStatusResult {
  project_id: number;
  requirement_id: string;
  mainline_req_id: string;
  last_mainline_version: string;
  last_action: 'synced' | 'ignored' | null;
  last_action_at: string | null;
}

export interface PaginatedFlowback {
  count: number;
  next: string | null;
  previous: string | null;
  results: FlowbackRecord[];
}

/* ------------------------------------------------------------------ */
/* API                                                                */
/* ------------------------------------------------------------------ */

export const registerFlowback = async (data: {
  project_id: number;
  requirement_id: string;
  subcategory: string;
}): Promise<FlowbackRecord> => {
  const r = await apiClient.post<FlowbackRecord>('/flowback/', data);
  return r.data;
};

export const listFlowback = async (params?: {
  status?: FlowbackStatus;
  project_id?: number;
  initiator?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
}): Promise<PaginatedFlowback> => {
  const r = await apiClient.get<PaginatedFlowback>('/flowback/', { params });
  return r.data;
};

export const getFlowbackDetail = async (id: number): Promise<FlowbackDetail> => {
  const r = await apiClient.get<FlowbackDetail>(`/flowback/${id}/`);
  return r.data;
};

export const reviewFlowback = async (
  id: number,
  action: 'sync_to_target' | 'ignore',
): Promise<FlowbackRecord> => {
  const r = await apiClient.post<FlowbackRecord>(`/flowback/${id}/review/`, { action });
  return r.data;
};

export const getFlowbackStats = async (params?: {
  dimension?: 'subcategory' | 'initiator' | 'project';
  date_from?: string;
  date_to?: string;
}): Promise<FlowbackStatsResponse> => {
  const r = await apiClient.get<FlowbackStatsResponse>('/flowback/stats/', { params });
  return r.data;
};

export const getLagging = async (
  projectId: number,
  subcategory?: string,
): Promise<{ project_id: number; items: LaggingRequirement[] }> => {
  const r = await apiClient.get<{ project_id: number; items: LaggingRequirement[] }>(
    `/flowback/projects/${projectId}/lagging/`,
    { params: subcategory ? { subcategory } : undefined },
  );
  return r.data;
};

export const getNewRequirements = async (
  projectId: number,
  subcategory?: string,
): Promise<{ project_id: number; root_project_id: number | null; root_project_name: string | null; items: NewRequirementItem[] }> => {
  const r = await apiClient.get<{ project_id: number; root_project_id: number | null; root_project_name: string | null; items: NewRequirementItem[] }>(
    `/flowback/projects/${projectId}/new-requirements/`,
    { params: subcategory ? { subcategory } : undefined },
  );
  return r.data;
};

export const getFlowbackEligibility = async (
  projectId: number,
  subcategory?: string,
): Promise<{ project_id: number; items: FlowbackEligibilityItem[] }> => {
  const r = await apiClient.get<{ project_id: number; items: FlowbackEligibilityItem[] }>(
    `/flowback/projects/${projectId}/eligibility/`,
    { params: subcategory ? { subcategory } : undefined },
  );
  return r.data;
};

export const syncUpdate = async (
  projectId: number,
  data: { requirement_id: string; subcategory?: string; action: 'sync_to_target' | 'ignore' | 'mark_synced' },
): Promise<RequirementSyncStatusResult> => {
  const r = await apiClient.post<RequirementSyncStatusResult>(
    `/flowback/projects/${projectId}/sync/`,
    data,
  );
  return r.data;
};
